import {
  type Context,
  useContextSelector,
} from '@fluentui/react-context-selector';
import { useMemo } from 'react';

type GenericContextSelectorsHook = <
  Value,
  Selectors extends Record<string, (contextValue: Value) => any>,
>(
  context: Context<Value>,
  selectors: Selectors,
) => { [K in keyof Selectors]: ReturnType<Selectors[K]> };

export type ContextSelectorsHook<Value> = <
  Selectors extends Record<string, (contextValue: Value) => any>,
>(
  selectors: Selectors,
) => { [K in keyof Selectors]: ReturnType<Selectors[K]> };

/**
 * Replacement for no longer supported useContextSelectors from @fluentui/react-context-selector
 *
 * Selectors object must have a stable set of keys on every render, because it is internally mapped
 * to multiple hook calls.
 *
 * Note: Original useContextSelectors had completely useless typing - it represents all selected values
 * with one generic argument (Record<Properties, SelectedValue>), so all selected values share one supertype.
 * Our signature fixes this, assigning return type of each selector separately to each selected value.
 */
export const useContextSelectors: GenericContextSelectorsHook = (
  context,
  selectors,
) => {
  // Order of iteration on object.keys is not guaranteed; it is mostly stable on object literals,
  // but if it would be ever changed and order of useContextSelector hooks changes there,
  // it may lead into very weird and hard to debug problems.
  // Memoizing sorted keys ensures order will never change, and if a set of keys change,
  // it will at least break in a much more obvious and predictable way (any change on key set
  // will immediately lead to accessing missing selector there or missing value in consuming code).
  // biome-ignore lint/correctness/useExhaustiveDependencies(selectors): intentional, see comment above
  const keys = useMemo(() => Object.keys(selectors).sort(), []);
  return keys.reduce((acc, key) => {
    // Fixed noUncheckedIndexedAccess NIM-19408
    acc[key] = useContextSelector(context, selectors[key]!);
    return acc;
  }, {} as any);
};
